import React from 'react';
import { CheckCircle, AlertTriangle } from 'lucide-react';

interface SecuritySettings {
  rateLimiting: boolean;
  ipWhitelist: boolean;
  apiKeyRotation: boolean;
  auditLogging: boolean;
  encryptionAtRest: boolean;
}

interface Props {
  securitySettings: SecuritySettings;
  setSecuritySettings: (settings: SecuritySettings) => void;
  onSaveSettings: () => void;
  loading: boolean;
}

const SecurityTab: React.FC<Props> = ({
  securitySettings,
  setSecuritySettings,
  onSaveSettings,
  loading,
}) => {
  const securityOptions = [
    {
      key: 'rateLimiting',
      title: 'Rate Limiting',
      description: 'Limit API requests per minute',
    },
    {
      key: 'ipWhitelist',
      title: 'IP Whitelist',
      description: 'Restrict access to specific IP addresses',
    },
    {
      key: 'apiKeyRotation',
      title: 'API Key Rotation',
      description: 'Automatically rotate API keys',
    },
    {
      key: 'auditLogging',
      title: 'Audit Logging',
      description: 'Log all DNS operations',
    },
    {
      key: 'encryptionAtRest',
      title: 'Encryption at Rest',
      description: 'Encrypt stored DNS data',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-6">
          Security Settings
        </h3>

        <div className="space-y-6">
          {securityOptions.map((option) => (
            <div key={option.key} className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {option.title}
                </h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {option.description}
                </p>
              </div>
              <input
                type="checkbox"
                checked={securitySettings[option.key as keyof SecuritySettings]}
                onChange={(e) =>
                  setSecuritySettings({
                    ...securitySettings,
                    [option.key]: e.target.checked,
                  })
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
              />
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onSaveSettings}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            Save Settings
          </button>
        </div>
      </div>

      {/* Security Alerts */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
          Recent Security Events
        </h3>

        <div className="space-y-3">
          <div className="flex items-start space-x-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-900 dark:text-green-100">
                API Key Rotation Completed
              </p>
              <p className="text-xs text-green-700 dark:text-green-300">2 hours ago</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                Rate Limit Exceeded
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300">
                IP: ************* - 1 day ago
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityTab;